import { BaseAttribute } from '../../types/attributes';
import { CmsProduct } from '../../types/cmsProduct';
import { groupProductAttributes } from '../../utils/helpers';

// Mirror AttributeKeyValue.tsx: a key-value attribute renders nothing for the
// BUNDLE_PRODUCT_OPTIONS relation, and otherwise only when at least one nested
// values[].value carries text.
const hasVisibleKeyValue = (
  attr: Extract<
    BaseAttribute,
    { __typename: 'ComponentAttributesAttrKeyValue' }
  >,
): boolean => {
  if (attr.name?.name === 'BUNDLE_PRODUCT_OPTIONS') {
    return false;
  }
  return (attr.value ?? []).some((kv) =>
    (kv?.values ?? []).some((v) => !!v?.value),
  );
};

const hasRenderableAttribute = (attributes?: BaseAttribute[]): boolean =>
  (attributes ?? []).some((attr) => {
    if (
      attr?.__typename === 'ComponentAttributesAttrPaymentType' ||
      attr?.__typename === 'ComponentAttributesAttrPaymentNetwork'
    ) {
      // Mirror AttributePayment.tsx: renders only for a non-empty value list.
      return Array.isArray(attr.value) && attr.value.length > 0;
    }
    if (attr?.__typename === 'ComponentAttributesAttrKeyValue') {
      return hasVisibleKeyValue(attr);
    }
    return false;
  });

// Mirrors the sections that <ProductContent /> (the PDP aside modal body) and
// its <ProductCardSticky /> actually render for a product. Returns false only
// when the modal would be "near-empty" — just the product name, logo and Apply
// CTA — which is the case CMS-1601 hides "View Details" for.
//
// Keep this in lockstep with ProductContent.tsx / ProductCardSticky.tsx and the
// attribute renderers (AttributeKeyValue.tsx, AttributePaymentType.tsx): each
// content branch below must match a section those components actually render.
export const hasPdpModalContent = (product?: CmsProduct): boolean => {
  if (!product) {
    return false;
  }

  // Both fields are rich text rendered through RichText (dangerouslySetInnerHTML),
  // so `.trim()` is deliberately a proxy for "has copy": it catches the emptied
  // shapes that actually occur in CMS data ('', null, ' ') but would count
  // markup-only content such as '<p></p>' as text.
  if (product.description?.trim() || product.long_description?.trim()) {
    return true;
  }

  // ProductCardSticky renders a clickable rating widget when rate_score >= 1.
  if ((product.rate_score ?? 0) >= 1) {
    return true;
  }

  // ProductContent drops falsy offers before deciding to render the rewards
  // block, so an array of only null entries is not content.
  const offers = [
    ...(product.welcome_offers ?? []),
    ...(product.exclusive_offers ?? []),
    ...(product.bundle_exclusive_offers ?? []),
  ].filter(Boolean);
  if (offers.length) {
    return true;
  }

  const { benefits, eligibility, fees, repayment } =
    groupProductAttributes(product);
  if (benefits.length || eligibility.length || fees.length || repayment.length) {
    return true;
  }

  return hasRenderableAttribute(product.attributes);
};
